import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../utils/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole = [] 
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  console.log('🛡️ ProtectedRoute check:', {
    loading,
    isAuthenticated,
    user: user?.email,
    userRole: user?.role,
    requiredRole,
    currentPath: location.pathname
  });

  // Show loading while auth is being initialized
  if (loading) {
    console.log('⏳ ProtectedRoute: Auth loading, showing loader...');
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontFamily: 'SF Pro, -apple-system, BlinkMacSystemFont, sans-serif' 
      }}>
        Loading...
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    console.log('🚫 ProtectedRoute: User not authenticated, redirecting to login');
    console.log('- isAuthenticated:', isAuthenticated);
    console.log('- user:', user);
    
    // Force clear any remaining auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('tokenExpires');
    
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  console.log('✅ ProtectedRoute: User authenticated, checking role requirements...');

  // Check role requirements
  if (requiredRole.length > 0 && !requiredRole.includes(user.role)) {
    console.log('🔄 ProtectedRoute: Role mismatch, redirecting based on user role');
    console.log('- Required roles:', requiredRole);
    console.log('- User role:', user.role);
    
    // Redirect based on user's actual role
    switch (user.role) {
      case 'physician':
        console.log('🩺 Redirecting to /physician');
        return <Navigate to="/physician" replace />;
      case 'specialist':
        console.log('🔬 Redirecting to /reviewer');
        return <Navigate to="/reviewer" replace />;
      case 'admin':
      case 'super-admin':
        console.log('👨‍💼 Redirecting to /admin');
        return <Navigate to="/admin" replace />;
      default:
        console.log('🏠 Redirecting to home');
        return <Navigate to="/" replace />;
    }
  }

  console.log('✅ ProtectedRoute: All checks passed, rendering children');
  return <>{children}</>;
};

export default ProtectedRoute;
